/** @jsx jsx */

import { get } from 'lodash';
import { Fragment, PureComponent } from 'react';

import { RelativeTime } from '@zapier/date';
import { jsx } from '@emotion/core';
import {
  Alert,
  Button,
  Grid,
  Icon,
  Margin,
  Surface,
  Text,
} from '@zapier/design-system';

import Markdown from 'app/developer-v3/components/Markdown';

import CommentEditor from '../CommentEditor';
import Styles from './Comment.styles';

import type { FormVariables } from './types';
import type { Comment as IssueComment } from 'app/developer-v3/hooks/useIssue';

type Props = {
  appId: number;
  accountOwnerEmail: string;
  appImageUrl?: string;
  appTitle?: string;
  comment: IssueComment;
  depth: number;
  isFirstItem?: boolean;
  isSaving: boolean;
  issueId: number;
  precedesEvent: boolean;
  onSubmit: (variables: FormVariables) => void;
};

type State = {
  isEditing: boolean;
};

class Comment extends PureComponent<Props, State> {
  state = {
    isEditing: false,
  };

  onToggleEditingClick = () => {
    this.setState(prevState => ({ isEditing: !prevState.isEditing }));
  };

  onUpdateComment = async (variables: FormVariables) => {
    try {
      await this.props.onSubmit(variables);
    } catch (error) {
      // eat it
    }
    this.onToggleEditingClick();
  };

  getAuthorType = () => {
    return get(this.props, ['comment', 'author', 'isStaff'], false)
      ? 'Zapier Staff'
      : 'Partner';
  };

  renderBody = () => {
    const { isEditing } = this.state;
    const { comment, isSaving } = this.props;

    return isEditing ? (
      <CommentEditor
        comment={comment}
        isSaving={isSaving}
        onCancelEdit={this.onToggleEditingClick}
        onSubmit={this.onUpdateComment}
        value={comment.markdownBody}
      />
    ) : (
      <Markdown source={comment.markdownBody} />
    );
  };

  renderError = () => {
    const errorMsg = get(this.props, ['comment', 'errorMsg']);
    return errorMsg && <Alert status="error">{errorMsg}</Alert>;
  };

  renderChildrenComments = () => {
    const {
      accountOwnerEmail,
      appId,
      comment,
      depth,
      isSaving,
      issueId,
      onSubmit,
      precedesEvent,
    } = this.props;
    const childComments = comment.children || [];
    return (
      childComments.length > 0 && (
        <div>
          {childComments.map((childComment, i: number) => {
            const isLastComment = childComments.length === i + 1;

            return (
              <Comment
                accountOwnerEmail={accountOwnerEmail}
                appId={appId}
                comment={childComment}
                depth={depth + 1}
                isSaving={isSaving}
                issueId={issueId}
                key={childComment.id}
                onSubmit={onSubmit}
                precedesEvent={isLastComment && precedesEvent}
              />
            );
          })}
        </div>
      )
    );
  };

  renderFooter = () => {
    const { isStaff } = this.props.comment.author;
    const logo = isStaff ? (
      <Icon color="Red4" name="zapierLogo" size={30} />
    ) : (
      <img
        alt="Integration Logo"
        src={this.props.appImageUrl}
        style={{ width: 30 }}
      />
    );
    const name = isStaff ? 'Zapier' : this.props.appTitle;

    return (
      <Fragment>
        {logo}
        <Text fontWeight={600} margin="0px 0px 0px 15px" tag="span">
          {name}
        </Text>
      </Fragment>
    );
  };

  render() {
    const { accountOwnerEmail, comment } = this.props;
    const { isEditing } = this.state;
    const authorType = this.getAuthorType();
    const { author } = comment;
    const showEditButton = !isEditing && accountOwnerEmail === author.email;
    const authorDisplayName = author.publicAlias || author.name;

    return (
      <Margin margin="10px 0px 20px 0px">
        <Surface borderColor="GrayWarm4">
          <section css={Styles.comment}>
            <header css={Styles.header(author.isStaff)}>
              <Grid columns="40px auto 150px" gap="15px">
                <Grid columns={1} gap="10px 15px">
                  <img alt={authorDisplayName} src={author.gravatar} />
                </Grid>
                <Grid columns={1} gap="5px 15px">
                  <Text color="inherit" tag="span" type="Body4">
                    {authorDisplayName}
                  </Text>
                  <Grid columns="auto 1fr" gap="10px">
                    <Text
                      color="inherit"
                      fontWeight={600}
                      tag="span"
                      type="Title"
                    >
                      {authorType}
                    </Text>
                  </Grid>
                </Grid>
                <Grid columns={1} gap="5px 15px">
                  <RelativeTime date={comment.createdAt} />
                  {showEditButton && (
                    <Button
                      color={author.isStaff ? 'secondary' : 'primary'}
                      onClick={this.onToggleEditingClick}
                      size="compact"
                    >
                      {isEditing ? 'Cancel Editing' : 'Edit Comment'}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </header>
            <div css={Styles.body}>
              {this.renderBody()}
              {this.renderError()}
            </div>
            <footer css={Styles.footer}>{this.renderFooter()}</footer>
          </section>
          {this.renderChildrenComments()}
        </Surface>
      </Margin>
    );
  }
}

export default Comment;
